import { useEffect } from 'react';
import useTripStore from '../store/useTripStore';
import { useDay } from './useTripStoreSelectors';
import {
  priceHotelStay,
  HotelStayPrice,
  HotelStayRoomsAvailability,
} from '../logic/priceHotelStay';

// A price of NaN (the room selection is unavailable) never carries a
// currency worth comparing on — nothing renders it (ComposeDayHotel shows
// "some rooms are no longer available" instead of an amount) — so two NaN
// prices are the same price regardless of which hook or reconcile path set
// the currency last (see useRoomSelectionRefresh, which also writes
// hotelPricing directly on its own "unavailable" branch).
function isSamePrice(
  a: HotelStayPrice | null | undefined,
  b: HotelStayPrice | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  if (Number.isNaN(a.price) && Number.isNaN(b.price)) return true;
  return a.price === b.price && a.currency === b.currency;
}

// US-058 — the hotel price of a day, computed outside the component. Mounted
// once per hotel day, same convention as useRoomSelectionRefresh(dayIndex,
// roomsData): a no-op for every day but the group's first, since only that
// day ever carries `selectedRoom`/`hotelNights`. `roomsData` is the same
// query result the day's own useHotelRoomAvailability call already fetched.
export function useTripHotelPricing(
  dayIndex: number,
  roomsData: HotelStayRoomsAvailability | undefined,
) {
  const day = useDay(dayIndex);
  const updateHotelPricing = useTripStore((state) => state.updateHotelPricing);

  const selection = day?.selectedRoom;
  const nights = day?.hotelNights ?? 0;
  // Only prices a resolved selection — never the cheapest-rooms fallback
  // priceHotelStay itself supports, or this would write a price a render
  // ahead of the day actually carrying `selectedRoom` (the auto-select
  // effect that decides it runs later in the same component).
  const priced = selection
    ? priceHotelStay(roomsData, selection, nights)
    : null;

  useEffect(() => {
    if (!priced || isSamePrice(priced, day?.hotelPricing)) return;
    updateHotelPricing(dayIndex, priced);
    // `priced`/`day` are read fresh from the closure on every call; only the
    // fields that decide whether a write is needed belong in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dayIndex,
    priced?.price,
    priced?.currency,
    day?.hotelPricing?.price,
    day?.hotelPricing?.currency,
    updateHotelPricing,
  ]);

  return priced;
}
